import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const field = "w-full bg-transparent border border-cream/30 rounded-sm px-3 py-2 text-sm text-cream";

const HOME_FIELDS = [
    ["made_kicker", "Island Gold products · small label"],
    ["made_title", "Island Gold products · title"],
    ["picks_kicker", "Bath picks · small label"],
    ["picks_title", "Bath picks · title"],
];

export function HomeSectionTitles() {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/public/home-sections").then(({ data }) => setForm(data)).catch(() => toast.error("Could not load titles."));
    }, []);
    if (!form) return <Loader2 className="animate-spin mt-8" size={18} />;
    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/owner/home-sections", form);
            setForm(data);
            toast.success("Homepage titles saved.");
        } catch {
            toast.error("Every title needs some text.");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="mt-6 max-w-2xl space-y-4" data-testid="owner-home-titles">
            {HOME_FIELDS.map(([k, label]) => (
                <label key={k} className="block text-sm text-cream">
                    {label}
                    <input className={`${field} mt-1`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        data-testid={`owner-home-${k}-input`} />
                </label>
            ))}
            <button onClick={save} disabled={saving} className="btn-pill btn-ghost text-[0.65rem]" data-testid="owner-home-titles-save-btn">
                {saving ? <Loader2 size={12} className="animate-spin" /> : null} Save
            </button>
        </div>
    );
}

function ReplyBox({ id, onSent, templates = [] }) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const send = async () => {
        setBusy(true);
        try {
            await api.post(`/owner/studio-inbox/${id}/reply`, { reply: text });
            toast.success("Reply emailed");
            setText("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send reply");
        } finally {
            setBusy(false);
            onSent();
        }
    };
    return (
        <div className="mt-3">
            {templates.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2" data-testid={`studio-msg-templates-${id}`}>
                    {templates.map((t, i) => (
                        <button key={i} type="button" onClick={() => setText(t.text)}
                            className="text-xs underline" data-testid={`studio-msg-template-${id}-${i}`}>
                            {t.label}
                        </button>
                    ))}
                </div>
            )}
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply by email…"
                className={field} data-testid={`studio-msg-reply-input-${id}`} />
            <button onClick={send} disabled={busy || !text.trim()} className="mt-2 btn-pill btn-ghost text-[0.6rem]"
                data-testid={`studio-msg-reply-btn-${id}`}>
                {busy ? <Loader2 size={12} className="animate-spin" /> : null} Send reply
            </button>
        </div>
    );
}

function TemplatesEditor({ templates, onSaved }) {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState(templates);
    const [saving, setSaving] = useState(false);
    useEffect(() => setRows(templates), [templates]);
    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/owner/reply-templates", { templates: rows.filter((r) => r.label.trim() && r.text.trim()) });
            onSaved(data.templates);
            toast.success("Templates saved.");
            setOpen(false);
        } catch {
            toast.error("Each template needs a short name and text.");
        } finally {
            setSaving(false);
        }
    };
    const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
    return (
        <div className="mt-3 text-sm text-cream" data-testid="reply-templates-editor">
            <button onClick={() => setOpen(!open)} className="underline text-xs" data-testid="reply-templates-toggle">
                {open ? "Close reply templates" : `Edit reply templates (${templates.length})`}
            </button>
            {open && (
                <div className="mt-3 space-y-3">
                    {rows.map((r, i) => (
                        <div key={i} className="grid md:grid-cols-[140px_1fr_auto] gap-2">
                            <input className={field} value={r.label} onChange={(e) => set(i, "label", e.target.value)} placeholder="Name" data-testid={`reply-template-label-${i}`} />
                            <textarea rows={2} className={field} value={r.text} onChange={(e) => set(i, "text", e.target.value)} data-testid={`reply-template-text-${i}`} />
                            <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-xs underline" data-testid={`reply-template-remove-${i}`}>Remove</button>
                        </div>
                    ))}
                    <div className="flex gap-3">
                        <button onClick={() => setRows([...rows, { label: "", text: "" }])} className="text-xs underline" data-testid="reply-template-add">Add template</button>
                        <button onClick={save} disabled={saving} className="btn-pill btn-ghost text-[0.6rem]" data-testid="reply-templates-save">Save templates</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export function StudioInbox() {
    const [templates, setTemplates] = useState([]);
    useEffect(() => {
        api.get("/owner/reply-templates").then(({ data }) => setTemplates(data.templates)).catch(() => {});
    }, []);
    const [data, setData] = useState(null);
    const load = () => api.get("/owner/studio-inbox").then(({ data }) => setData(data)).catch(() => toast.error("Could not load inbox."));
    useEffect(() => { load(); }, []);
    const mark = async (m) => {
        try {
            await api.post(`/owner/studio-inbox/${m.id}/read`, { read: m.status === "new" });
            load();
        } catch {
            toast.error("Could not update message.");
        }
    };
    if (!data) return <Loader2 className="animate-spin mt-8" size={18} />;
    return (
        <div className="mt-6 max-w-3xl" data-testid="owner-studio-inbox">
            <p className="text-sm text-cream" data-testid="studio-inbox-count">
                {data.messages.length} note{data.messages.length === 1 ? "" : "s"} · {data.unread} unread
            </p>
            <TemplatesEditor templates={templates} onSaved={setTemplates} />
            {data.messages.length === 0 && <p className="mt-4 text-sm text-cream">No notes yet.</p>}
            <ul className="mt-4 space-y-3">
                {data.messages.map((m) => (
                    <li key={m.id} className="p-4 border border-cream/20 rounded-sm text-sm text-cream" data-testid={`studio-msg-${m.id}`}>
                        <div className="flex flex-wrap justify-between gap-2">
                            <span className="font-semibold">
                                {m.status === "new" ? "● " : ""}{m.title || m.module.replace(/_/g, " ")}
                            </span>
                            <span>{new Date(m.created_at).toLocaleString()}</span>
                        </div>
                        <p className="mt-1">{m.name || "Anonymous"}{m.email ? ` · ${m.email}` : ""}{m.rating ? ` · ${m.rating}★` : ""}</p>
                        <p className="mt-2 whitespace-pre-wrap">{m.body}</p>
                        {m.media_url && <a href={m.media_url} target="_blank" rel="noreferrer" className="underline mt-1 inline-block">Video link</a>}
                        {(m.replies || []).map((r, i) => (
                            <p key={i} className="mt-2 text-xs whitespace-pre-wrap" data-testid={`studio-msg-reply-${m.id}-${i}`}>
                                You replied {new Date(r.at).toLocaleString(undefined, { timeZoneName: "short" })}{r.sent ? "" : " (not sent)"}: {r.text}
                            </p>
                        ))}
                        {m.email && <ReplyBox id={m.id} onSent={load} templates={templates} />}
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                            <button onClick={() => mark(m)} className="btn-pill btn-ghost text-[0.6rem]" data-testid={`studio-msg-toggle-${m.id}`}>
                                {m.status === "new" ? "Mark as read" : "Mark unread"}
                            </button>
                            <span className="text-xs">{m.emailed ? "Emailed" : "Not emailed (email key missing)"}</span>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function AnnounceBox({ count }) {
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const [last, setLast] = useState(null);
    const [preview, setPreview] = useState(null);
    const showPreview = async () => {
        try {
            const { data } = await api.post("/owner/insiders/preview", { subject, message, origin: window.location.origin });
            setPreview(data);
        } catch {
            toast.error("Could not build preview");
        }
    };
    const send = async () => {
        if (!window.confirm(`Send this email to all ${count} Insiders now?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/owner/insiders/announce", { subject, message, origin: window.location.origin });
            setLast(data);
            setPreview(null);
            toast.success(`Sent to ${data.sent} of ${data.total}`);
            setSubject("");
            setMessage("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="mt-4 mb-6 p-4 border border-cream/20 rounded-sm space-y-2" data-testid="insiders-announce">
            <p className="text-sm text-cream font-semibold">Send an early-drop email to every Insider</p>
            <input className={field} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="insiders-announce-subject" />
            <textarea rows={4} className={field} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Your message…" data-testid="insiders-announce-message" />
            <div className="flex flex-wrap gap-2">
                <button onClick={showPreview} disabled={!subject.trim() || !message.trim()} className="btn-pill btn-ghost text-[0.6rem]" data-testid="insiders-announce-preview-btn">
                    Preview
                </button>
                <button onClick={send} disabled={busy || !count || !subject.trim() || !message.trim()} className="btn-pill btn-ghost text-[0.6rem]" data-testid="insiders-announce-send">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : null} Send to {count} Insider{count === 1 ? "" : "s"}
                </button>
            </div>
            {preview && (
                <div className="mt-2" data-testid="insiders-announce-preview">
                    <p className="text-xs text-cream">Subject: <strong>{preview.subject}</strong> · goes to {preview.recipients}</p>
                    <iframe title="Email preview" srcDoc={preview.html} sandbox="" className="mt-2 w-full h-64 bg-white rounded-sm" />
                </div>
            )}
            {last && <p className="text-xs text-cream" data-testid="insiders-announce-result">Sent {last.sent} of {last.total}{last.failed.length ? ` · ${last.failed.length} failed` : ""}</p>}
        </div>
    );
}

function UnsubscribedList() {
    const [d, setD] = useState(null);
    useEffect(() => {
        api.get("/owner/insiders/unsubscribed").then(({ data }) => setD(data)).catch(() => {});
    }, []);
    if (!d) return null;
    return (
        <div className="mt-8" data-testid="owner-insiders-unsubscribed">
            <p className="font-semibold">Left the list: {d.unsubscribed.length} · still active: {d.active}</p>
            {Object.keys(d.by_month).length > 0 && (
                <p className="mt-1 text-xs" data-testid="unsubscribed-by-month">
                    By month: {Object.entries(d.by_month).map(([m, n]) => `${m} — ${n}`).join(" · ")}
                </p>
            )}
            {Object.keys(d.by_reason || {}).length > 0 && (
                <p className="mt-1 text-xs" data-testid="unsubscribed-by-reason">
                    Why: {Object.entries(d.by_reason).map(([r, n]) => `${r} — ${n}`).join(" · ")}
                </p>
            )}
            <ul className="mt-2 space-y-1">
                {d.unsubscribed.map((r) => (
                    <li key={r.email}>
                        {r.email} · joined {new Date(r.created_at).toLocaleDateString()} · left {new Date(r.unsubscribed_at).toLocaleDateString(undefined, { timeZoneName: "short" })}
                        {r.leave_reason ? ` · ${r.leave_reason}${r.leave_reason_other ? `: “${r.leave_reason_other}”` : ""}` : ""}
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function InsidersList() {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get("/owner/insiders").then(({ data }) => setRows(data.insiders)).catch(() => setRows([]));
    }, []);
    if (!rows) return <Loader2 className="animate-spin mt-8" size={18} />;
    return (
        <div className="mt-6 text-sm text-cream" data-testid="owner-insiders-list">
            <p>{rows.length} Island Gold Insider{rows.length === 1 ? "" : "s"} (separate from the waitlist)</p>
            <AnnounceBox count={rows.length} />
            <ul className="mt-3 space-y-1">
                {rows.map((r) => (
                    <li key={r.email}>{r.email} · {new Date(r.created_at).toLocaleDateString()}</li>
                ))}
            </ul>
            <UnsubscribedList />
        </div>
    );
}

export function StudioFormSettings() {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/owner/studio-form")
            .then(({ data }) => setForm({ ...data, owner_text: (data.owner_recipients || []).join(", ") }))
            .catch(() => toast.error("Could not load studio form settings."));
    }, []);

    if (!form) return <Loader2 className="animate-spin mt-8" size={18} />;

    const save = async () => {
        setSaving(true);
        try {
            const owner_recipients = form.owner_text.split(",").map((e) => e.trim()).filter(Boolean);
            const { data } = await api.put("/owner/studio-form", {
                intro: form.intro,
                confirmation: form.confirmation,
                owner_recipients,
            });
            setForm({ ...data, owner_text: data.owner_recipients.join(", ") });
            toast.success("Studio form saved.");
        } catch (err) {
            toast.error("Could not save — check the email addresses.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-6 max-w-2xl space-y-5" data-testid="owner-studio-form-settings">
            <label className="block text-sm text-cream">
                Form intro
                <textarea rows={3} className={`${field} mt-1`} value={form.intro}
                    onChange={(e) => setForm({ ...form, intro: e.target.value })}
                    data-testid="owner-studio-intro-input" />
            </label>
            <label className="block text-sm text-cream">
                Line shown after sending
                <textarea rows={2} className={`${field} mt-1`} value={form.confirmation}
                    onChange={(e) => setForm({ ...form, confirmation: e.target.value })}
                    data-testid="owner-studio-confirmation-input" />
            </label>
            <label className="block text-sm text-cream">
                Owner alert emails (comma separated)
                <input className={`${field} mt-1`} value={form.owner_text}
                    onChange={(e) => setForm({ ...form, owner_text: e.target.value })}
                    data-testid="owner-studio-recipients-input" />
            </label>
            <div className="text-sm text-cream" data-testid="owner-studio-creator-recipients">
                Creator alert emails (each creator can change their own in their dashboard):{" "}
                {(form.creator_recipients || []).map((c) => c.notify_email).join(", ") || "none"}
            </div>
            <button onClick={save} disabled={saving} className="btn-pill btn-ghost text-[0.65rem]"
                data-testid="owner-studio-save-btn">
                {saving ? <Loader2 size={12} className="animate-spin" /> : null} Save
            </button>
        </div>
    );
}
